import React, { useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
  isAfter,
  isBefore,
  startOfDay,
  subDays,
  parseISO,
} from 'date-fns';
import { el } from 'date-fns/locale';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  CheckCircle2,
  CalendarRange,
  Clock,
  Sparkles,
} from 'lucide-react';

interface HistoryCalendarProps {
  selectedDates: string[]; // Array of YYYY-MM-DD
  onDatesChange: (dates: string[]) => void;
  savedDates?: string[]; // Dates that exist in storage
  /**
   * Latest selectable day, YYYY-MM-DD. Defaults to today, but a NAERAS bid is
   * always *for* a future day — the one calculated today targets tomorrow — so
   * the page raises this to the newest bid it holds. Without that the most
   * recent offer is greyed out and unreachable.
   */
  maxDate?: string;
}

export const HistoryCalendar: React.FC<HistoryCalendarProps> = ({
  selectedDates,
  onDatesChange,
  savedDates = [],
  maxDate,
}) => {
  const today = startOfDay(new Date());
  const lastSelectableDay = maxDate ? startOfDay(parseISO(maxDate)) : today;
  const [currentMonth, setCurrentMonth] = useState<Date>(today);
  const [rangeStart, setRangeStart] = useState<string | null>(null);

  // Month navigation
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => {
    const next = addMonths(currentMonth, 1);
    if (!isAfter(startOfMonth(next), lastSelectableDay)) {
      setCurrentMonth(next);
    }
  };

  const isNextMonthDisabled = isAfter(startOfMonth(addMonths(currentMonth, 1)), lastSelectableDay);

  // Calendar matrix calculation
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday start
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const weekDays = ['Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ', 'Κυρ'];

  // Handle click on a date
  const handleDateClick = (date: Date) => {
    if (isAfter(startOfDay(date), lastSelectableDay)) return;

    const dateStr = format(date, 'yyyy-MM-dd');

    if (!rangeStart) {
      // First click: select single day and set as potential range start
      setRangeStart(dateStr);
      onDatesChange([dateStr]);
    } else {
      // Second click: select range between rangeStart and clicked date
      const start = parseISO(rangeStart);
      const end = date;
      const [intervalStart, intervalEnd] = isBefore(start, end) ? [start, end] : [end, start];

      const rangeDays = eachDayOfInterval({ start: intervalStart, end: intervalEnd });
      const rangeDateStrings = rangeDays.map((d) => format(d, 'yyyy-MM-dd'));

      onDatesChange(rangeDateStrings);
      setRangeStart(null);
    }
  };

  // Quick preset shortcuts
  const selectToday = () => {
    const todayStr = format(today, 'yyyy-MM-dd');
    setRangeStart(null);
    onDatesChange([todayStr]);
    setCurrentMonth(today);
  };

  const selectYesterday = () => {
    const yesterday = subDays(today, 1);
    const yesterdayStr = format(yesterday, 'yyyy-MM-dd');
    setRangeStart(null);
    onDatesChange([yesterdayStr]);
    setCurrentMonth(yesterday);
  };

  const selectLast7Days = () => {
    const start = subDays(today, 6);
    const daysArr = eachDayOfInterval({ start, end: today });
    setRangeStart(null);
    onDatesChange(daysArr.map((d) => format(d, 'yyyy-MM-dd')));
    setCurrentMonth(today);
  };

  const selectLast30Days = () => {
    const start = subDays(today, 29);
    const daysArr = eachDayOfInterval({ start, end: today });
    setRangeStart(null);
    onDatesChange(daysArr.map((d) => format(d, 'yyyy-MM-dd')));
    setCurrentMonth(today);
  };

  const selectCurrentMonth = () => {
    const start = startOfMonth(today);
    const daysArr = eachDayOfInterval({ start, end: today });
    setRangeStart(null);
    onDatesChange(daysArr.map((d) => format(d, 'yyyy-MM-dd')));
    setCurrentMonth(today);
  };

  return (
    <div className="rounded-3xl border border-gray-200/80 bg-white p-6 shadow-xl shadow-gray-900/5 transition-all">
      {/* Header with Quick Presets */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-5">
        <div>
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">
              Επιλογή Ημερομηνίας ή Διαστήματος
            </h2>
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            Επιλέξτε μία ημέρα ή κάντε κλικ σε δύο ημερομηνίες για επιλογή διαστήματος. Οι
            ημέρες με πράσινη κουκκίδα έχουν υπολογισμένη προσφορά.
          </p>
        </div>

        {/* Quick Presets Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={selectToday}
            className="flex items-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50/80 px-3 py-1.5 text-xs font-bold text-blue-700 hover:bg-blue-100 transition shadow-xs"
          >
            <Clock className="h-3.5 w-3.5" />
            Σήμερα
          </button>
          <button
            onClick={selectYesterday}
            className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition shadow-xs"
          >
            Χθες
          </button>
          <button
            onClick={selectLast7Days}
            className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition shadow-xs"
          >
            Τελευταίες 7 Ημέρες
          </button>
          <button
            onClick={selectLast30Days}
            className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition shadow-xs"
          >
            Τελευταίες 30 Ημέρες
          </button>
          <button
            onClick={selectCurrentMonth}
            className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition shadow-xs"
          >
            Τρέχων Μήνας
          </button>
        </div>
      </div>

      {/* Month Navigation & Current View */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-bold capitalize text-gray-900">
          {format(currentMonth, 'LLLL yyyy', { locale: el })}
        </h3>

        <div className="flex items-center gap-1">
          <button
            onClick={prevMonth}
            className="flex h-8 w-8 items-center justify-center rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition"
            aria-label="Προηγούμενος Μήνας"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={nextMonth}
            disabled={isNextMonthDisabled}
            className={`flex h-8 w-8 items-center justify-center rounded-xl border border-gray-200 transition ${
              isNextMonthDisabled
                ? 'opacity-40 cursor-not-allowed text-gray-400'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            aria-label="Επόμενος Μήνας"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-2 mb-2 text-center text-xs font-bold text-gray-400">
        {weekDays.map((day) => (
          <div key={day} className="py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const isSelected = selectedDates.includes(dateStr);
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isFuture = isAfter(startOfDay(day), lastSelectableDay);
          const isCurrentDay = isToday(day);
          const hasSavedData = savedDates.includes(dateStr);

          // Range endpoints
          const isRangeEndpoint =
            selectedDates.length > 1 &&
            (dateStr === selectedDates[0] || dateStr === selectedDates[selectedDates.length - 1]);

          return (
            <button
              key={dateStr}
              type="button"
              disabled={isFuture}
              onClick={() => handleDateClick(day)}
              className={`relative flex h-11 flex-col items-center justify-center rounded-2xl text-xs font-semibold transition-all duration-150 ${
                isFuture
                  ? 'cursor-not-allowed opacity-25 text-gray-400 bg-gray-50/50'
                  : isSelected
                  ? isRangeEndpoint
                    ? 'bg-blue-600 text-white font-bold shadow-md shadow-blue-600/30 ring-2 ring-blue-600 ring-offset-2'
                    : 'bg-blue-500/90 text-white font-bold'
                  : !isCurrentMonth
                  ? 'text-gray-300 hover:bg-gray-50'
                  : isCurrentDay
                  ? 'border border-blue-400 bg-blue-50/40 text-blue-700 font-bold hover:bg-blue-50'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span>{format(day, 'd')}</span>

              {/* Status / Saved Dot */}
              <div className="flex items-center gap-0.5 mt-0.5">
                {hasSavedData && (
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      isSelected ? 'bg-amber-300' : 'bg-emerald-500'
                    }`}
                    title="Αποθηκευμένη Προσφορά"
                  />
                )}
                {isCurrentDay && !isSelected && (
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-600" title="Σήμερα" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected Info & Range Hint */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gray-50 p-3.5 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-blue-600" />
          <span>
            Επιλεγμένες ημέρες:{' '}
            <strong className="text-gray-900 font-bold">{selectedDates.length}</strong>
            {selectedDates.length > 0 && (
              <span className="ml-1 text-gray-500">
                ({format(parseISO(selectedDates[0]), 'd/M/yyyy')}
                {selectedDates.length > 1 &&
                  ` έως ${format(parseISO(selectedDates[selectedDates.length - 1]), 'd/M/yyyy')}`}
                )
              </span>
            )}
          </span>
        </div>

        {rangeStart && (
          <span className="animate-pulse rounded-lg bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800">
            Κάντε κλικ στη δεύτερη ημερομηνία για ολοκλήρωση διαστήματος
          </span>
        )}
      </div>
    </div>
  );
};

export default HistoryCalendar;
